<template>
  <v-row class="justify-center">
    <insurance-summary-dialog ref="insuranceSummaryDialog" />
    <update-draft-dialog 
      ref="updateDraftDialog" 
      :household="household" 
      :plan="plan" 
      @on-debt-updated="$emit('on-debt-updated', $event)"
      @on-policy-updated="$emit('on-policy-updated', $event)" 
      @on-plan-updated="$emit('on-plan-updated', $event)"
    />
    <checkin-dialog
      ref="checkinDialog"
      :household="household"
      :plan="plan"
      @on-debt-updated="$emit('on-debt-updated', $event)"
      @on-policy-updated="$emit('on-policy-updated', $event)"
      @on-plan-updated="$emit('on-plan-updated', $event)"
    />
    <v-dialog
      v-model="truthCalculatorDialog"
      max-width="600px"
      content-class="elevation-0"
    >
      <truth-calculator />
    </v-dialog>

    <v-dialog
      v-model="pipelineDialog"
      max-width="500px"
      :persistent="savingPipeline"
    >
      <v-card>
        <v-card-title>Update Pipeline</v-card-title>
        <v-card-text>
          <v-row no-gutters>
            <v-col cols="12" class="mb-2">
              <div class="caption">Status</div>
              <v-select
                v-model="pipelineStatus"
                :items="pipelineStatuses"
                outlined
                hide-details
                dense
                clearable
                :disabled="savingPipeline"
              />
            </v-col>
            <v-col cols="12" class="mb-2">
              <div class="caption">Opportunity Value</div>
              <v-text-field
                v-model="pipelineValue"
                outlined
                dense
                clearable
                hide-details
                :disabled="savingPipeline"
              />
            </v-col>
            <v-col cols="6" class="pr-1">

              <div class="caption">Date</div>
              <v-menu
                ref="pipelineDateMenu"
                v-model="pipelineDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="pipelineDateFormatted"
                    :disabled="savingPipeline"
                    dense
                    outlined
                    placeholder="Choose..."
                    v-bind="attrs"
                    v-on="on"
                    @blur="pipelineDate = parseDate(pipelineDateFormatted)"
                  />
                </template>
                <v-date-picker v-model="pipelineDate" no-title scrollable @input="pipelineDateMenu = false" />
              </v-menu>

            </v-col>
            <v-col cols="6" class="pl-1">

              <div class="caption">Close Date</div>
              <v-menu
                ref="pipelineCloseDateMenu"
                v-model="pipelineCloseDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :disabled="(pipelineStatus !== 'WIN' && pipelineStatus !== 'LOSS') || savingPipeline"
                    v-model="pipelineCloseDateFormatted"
                    dense
                    outlined
                    :placeholder="(pipelineStatus !== 'WIN' && pipelineStatus !== 'LOSS' ? 'Not Closed' : 'Choose...')"
                    v-bind="attrs"
                    v-on="on"
                    @blur="pipelineCloseDate = parseDate(pipelineCloseDateFormatted)"
                  />
                </template>
                <v-date-picker v-model="pipelineCloseDate" no-title scrollable @input="pipelineCloseDateMenu = false" />
              </v-menu>

            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="pipelineDialog = false" :disabled="savingPipeline">Close</v-btn>
          <v-btn color="primary" @click="savePipeline" :loading="savingPipeline">Save</v-btn>
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>
    

    <v-col
      md="8"
      lg="9"
    >
      <v-row class="align-center justify-center mb-4">
        <v-col cols="12" sm="4" lg="3" class="py-0">
          <v-card flat class="primary" style="height: 120px;">
            <v-card-text class="py-2 align-center justify-center d-flex flex-column fill-height white--text">
              <div class="subtitle-2 text-center">Money Saved</div>
              <div class="display-1 text-center">{{ moneySaved | currencyRounded }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="4" lg="3" class="py-0">
          <v-card flat class="primary" style="height: 120px;">
            <v-card-text class="py-2 align-center justify-center d-flex flex-column fill-height white--text">
              <div class="subtitle-2 text-center">Financial Freedom In</div>
              <div class="display-1 text-center">{{ debtFreeTime }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="4" lg="3" class="py-0">
          <v-card flat class="primary" style="height: 120px;">
            <v-card-text class="py-2 align-center justify-center d-flex flex-column fill-height white--text">
              <div class="subtitle-2 text-center mb-2">Policy Replenished In</div>
              <div class="display-1 text-center">{{ policyReplenishedTime }}</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row v-if="plan.data.debts && plan.data.policies" class="mb-4">
        <v-col cols="12">
          <debt-chart :plan="plan" />
        </v-col>
      </v-row>

      <v-row class="align-center justify-center mb-4">
        <v-col cols="12" class="py-0 text-center overline grey--text">
          At Age 70
        </v-col>
        <v-col cols="12" sm="4" lg="3" class="py-0">
          <v-card flat class="secondary" style="height: 120px;">
            <v-card-text class="py-2 align-center justify-center d-flex flex-column fill-height white--text">
              <div class="subtitle-2 text-center">Death Benefit</div>
              <div class="display-1 text-center">{{ at70.deathBenefit | currencyRounded }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="4" lg="3" class="py-0">
          <v-card flat class="secondary" style="height: 120px;">
            <v-card-text class="py-2 align-center justify-center d-flex flex-column fill-height white--text">
              <div class="subtitle-2 text-center">Cash Value</div>
              <div class="display-1 text-center">{{ at70.cashValue | currencyRounded }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="4" lg="3" class="py-0">
          <v-card flat class="secondary" style="height: 120px;">
            <v-card-text class="py-2 align-center justify-center d-flex flex-column fill-height white--text">
              <div class="subtitle-2 text-center mb-2">Savings</div>
              <div class="display-1 text-center">{{ at70.savings | currencyRounded }}</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-divider class="mb-4 mt-8" />

      <v-row>
        <v-col cols="12">
          <v-row no-gutters>
            <v-col />
            <v-col cols="3" class="px-2">
              <div class="text-center overline grey--text">Interest Volume</div>
            </v-col>
            <v-col cols="3" class="px-2">
              <div class="text-center overline grey--text">Payoff</div>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-row class="comparisonContainer">
        <v-col cols="12" class="pa-0">
          <v-row no-gutters class="align-center faCompareToggle pa-3" @click="showCurrentDebtInterest = !showCurrentDebtInterest">
            <v-col cols="6" class="title grey--text text--darken-3">
              <v-icon class="ml-n1 mr-1" v-text="'mdi-chevron-' + (showCurrentDebtInterest ? 'down' : 'right')" />Current Liability & Interest
            </v-col>
            <v-col cols="3" class="px-2">
              <div class="text-center mt-n1 error--text"><b>{{ interestVolume }}%</b></div>
            </v-col>
            <v-col cols="3" class="px-2">
              <div class="text-center error--text"><b>{{ minTime }}</b></div>
            </v-col>
          </v-row>
          <v-row no-gutters v-if="showCurrentDebtInterest" class="mb-8">
            <v-col cols="12" >
              <debt-table :plan="plan" min @on-open-debt="$emit('on-open-min-debt', $event)" />
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" class="pa-0">
          <v-row no-gutters class="align-center faCompareToggle pa-3" @click="showCapitalOptimizationSolution = !showCapitalOptimizationSolution">
            <v-col cols="6" class="title grey--text text--darken-3">
              <v-icon class="ml-n1 mr-1" v-text="'mdi-chevron-' + (showCapitalOptimizationSolution ? 'down' : 'right')" />Capital Optimization Solution
            </v-col>
            <v-col cols="3" class="px-2">
              <div class="text-center primary--text">
                <!-- <b>{{ planInterestVolume }}%</b> -->
              </div>
            </v-col>
            <v-col cols="3" class="px-2">
              <div class="text-center primary--text"><b>{{ debtFreeTime }}</b></div>
            </v-col>
          </v-row>
          <v-row no-gutters v-if="showCapitalOptimizationSolution" class="mb-8">
            <v-col cols="12" >
              <debt-table :plan="plan" @on-open-debt="$emit('on-open-plan-debt', $event)" />
            </v-col>
          </v-row>
        </v-col>
      </v-row>

    </v-col>
    <v-col
      md="4"
      lg="3"
    >
      <v-row no-gutters class="mb-6 align-center justify-center" v-if="status == 'DRAFT' || status == 'PENDING'">
        <v-col>
          <v-select
            v-model="status"
            :items="availableStatuses"
            outlined
            dense
            hide-details
            class="py-0"
          >
            <template v-slot:append-item>
              <v-divider class="mb-2" />
              <v-list-item @click="finalize">
                <v-list-item-content>
                  <v-list-item-title>
                    <v-icon small class="mr-2">mdi-lock</v-icon>
                    Finalize
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-select>
        </v-col>
      </v-row>
      <v-row no-gutters class="mb-6 flex-nowrap">
        <v-col class="pr-1">
          <v-btn v-if="plan.status == 'ACTIVE'" class="elevation-0" outlined block color="primary" @click="$refs.checkinDialog.open()"><v-icon small class="mr-2">mdi-account-details</v-icon> Check In</v-btn>
          <v-btn v-else-if="plan.status != 'REPLACED'" class="elevation-0" outlined block color="primary" @click="$refs.updateDraftDialog.open()"><v-icon small class="mr-2">mdi-clipboard-list</v-icon> Update</v-btn>
        </v-col>
        <v-col class="pl-1">
          <v-btn class="elevation-0" :outlined="(plan.status != 'ACTIVE' ? true : false)" block color="primary" @click="downloadPlan()"><v-icon small class="mr-2">mdi-file-pdf</v-icon> Download</v-btn>
        </v-col>
        <v-col class="flex-shrink-1 flex-grow-0 pl-2">
          <v-tooltip top>
            <template v-slot:activator="{on}">
              <v-btn v-on="on" elevation="0" color="grey darken-1" outlined style="min-width:30px;" @click="pipelineDialog = true"><v-icon>mdi-finance</v-icon></v-btn>
            </template>
            <span>
              Pipeline
            </span>
          </v-tooltip>
        </v-col>
      </v-row>
      <v-row no-gutters class="flex-column">
        <v-col>
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr class="grey lighten-4">
                  <th class="text-left pl-0" colspan="2">
                    <v-row no-gutters class="flex-nowrap">
                      <v-col class="black--text pl-4">Plan Details</v-col>
                    </v-row>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="faSummaryTitle">Start Date</td>
                  <td class="faSummaryDesc text-right">{{ $moment(plan.start_date).format('MMM YYYY') }}</td>
                </tr>
                <tr>
                  <td class="faSummaryTitle">Additional Capital</td>
                  <td class="faSummaryDesc text-right">{{ plan.additional_capital | currencyRounded }}</td>
                </tr>
                <tr v-if="plan.additional_policy_replenishment">
                  <td class="faSummaryTitle">Additional Policy Replenishment</td>
                  <td class="faSummaryDesc text-right">{{ plan.additional_policy_replenishment | currencyRounded }}</td>
                </tr>
              </tbody>
              <thead>
                <tr class="faSummaryToggle grey lighten-4" @click="showRequiredCapital = !showRequiredCapital">
                  <th class="text-left pl-0" colspan="2">
                    <v-row no-gutters class="flex-nowrap">
                      <v-col class="black--text"><v-icon class="black--text" small v-text="(showRequiredCapital ? 'mdi-chevron-down' : 'mdi-chevron-right')" />Required Capital</v-col>
                      <v-col class="text-right flex-shrink-1 flex-grow-0 caption grey--text text--darken-1 text-no-wrap">{{ totalYearly | currencyRounded }} / Year</v-col>
                    </v-row>
                  </th>
                </tr>
              </thead>
              <tbody v-if="showRequiredCapital">
                <tr>
                  <td class="faSummaryTitle">Monthly</td>
                  <td class="faSummaryDesc text-right">{{ monthlyCapital | currencyRounded }}</td>
                </tr>
                <tr>
                  <td class="faSummaryTitle">Yearly</td>
                  <td class="faSummaryDesc text-right">{{ yearlyCapital | currencyRounded }}</td>
                </tr>
              </tbody>
              <thead>
                <tr class="faSummaryToggle grey lighten-4" @click="showPolicies = !showPolicies">
                  <th class="text-left pl-0" colspan="2">
                    <v-row no-gutters class="flex-nowrap">
                      <v-col class="black--text"><v-icon class="black--text" small v-text="(showPolicies ? 'mdi-chevron-down' : 'mdi-chevron-right')" />Policies In This Plan</v-col>
                      <v-col class="text-right flex-shrink-1 flex-grow-0 caption grey--text text--darken-1 text-no-wrap">{{ Object.keys(plan.data.policies).length }}</v-col>
                    </v-row>
                  </th>
                </tr>
              </thead>
              <tbody v-if="showPolicies">
                <tr
                  v-for="policy in plan.data.policies"
                  :key="'prd-fa-policy-' + policy.id"
                  class="faSummaryCell"
                >
                  <td class="faSummaryTitle">{{ policy.name }}</td>
                  <td class="faSummaryDesc text-right">{{ policy.premium | currencyRounded }}</td>
                </tr>
              </tbody>
              <thead>
                <tr class="faSummaryToggle grey lighten-4" @click="showDebts = !showDebts">
                  <th class="text-left pl-0" colspan="2">
                    <v-row no-gutters class="flex-nowrap">
                      <v-col class="black--text"><v-icon class="black--text" small v-text="(showDebts ? 'mdi-chevron-down' : 'mdi-chevron-right')" />Liabilites In This Plan</v-col>
                      <v-col class="text-right flex-shrink-1 flex-grow-0 caption grey--text text--darken-1 text-no-wrap">{{ plan.data.total_debt_principal | currencyRounded }}</v-col>
                    </v-row>
                  </th>
                </tr>
              </thead>
              <tbody v-if="showDebts">
                <tr
                  v-for="debt in plan.data.debts"
                  :key="'prd-fa-debt-' + debt.id"
                  class="faSummaryCell"
                >
                  <td class="faSummaryTitle">{{ debt.name }}</td>
                  <td class="faSummaryDesc text-right">{{ debt.balance | currencyRounded }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-col>
        <v-col class="mt-8">
          <v-btn block color="secondary" outlined style="height: 50px;" class="d-block" @click="$refs.insuranceSummaryDialog.open(plan.data.yearly_summary)">
            <v-row no-gutters class="flex-nowrap align-center">
              <v-col class="flex-shrink-1 flex-grow-0 mr-4"><v-icon>mdi-clipboard-list</v-icon></v-col>
              <v-col class="text-left" style="white-space: normal;">Hypothetical Insurance Summary</v-col>
            </v-row>
          </v-btn>
        </v-col>
        <v-col class="mt-4">
          <v-btn block color="secondary" outlined style="height: 50px;" class="d-block" @click="truthCalculatorDialog = true">
            <v-row no-gutters class="flex-nowrap align-center">
              <v-col class="flex-shrink-1 flex-grow-0 mr-4"><v-icon>mdi-calculator</v-icon></v-col>
              <v-col class="text-left" style="white-space: normal;">Truth Calculator</v-col>
            </v-row>
          </v-btn>
        </v-col>
        <!--<v-col class="mt-4">
          <v-btn block color="secondary" outlined style="height: 50px;" class="d-block">
            <v-row no-gutters class="flex-nowrap align-center">
              <v-col class="flex-shrink-1 flex-grow-0 mr-4"><v-icon>mdi-calendar-clock</v-icon></v-col>
              <v-col class="text-left" style="white-space: normal;">Client Payment Schedule</v-col>
            </v-row>
          </v-btn>
        </v-col>-->
        <v-col class="mt-8" v-if="oldPlans.length > 1">
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr class="grey lighten-4">
                  <th class="text-left pl-0" colspan="2">
                    <v-row no-gutters class="flex-nowrap">
                      <v-col class="black--text pl-4">Other Versions</v-col>
                    </v-row>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="p in oldPlans"
                  :key="'old-plan-' + p.id"
                  class="faSummaryCell otherPlans"
                  :class="{'active':plan.id == p.id}"
                  @click="switchPlan(p)"
                >
                  <td class="faSummaryTitle"><a @click="switchPlan(p)">{{ $moment(p.start_date).format('L') }}</a></td>
                  <td class="faSummaryDesc text-right">
                    <v-chip x-small label>{{ ( (p.original_id == p.id || !p.original_id) ? 'ORIGINAL' : p.status) }}</v-chip>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>


<script>
import DebtChart from './DebtChart'
import DebtTable from './DebtTable'
import InsuranceSummaryDialog from './InsuranceSummaryDialog'
import UpdateDraftDialog from './UpdateDraftDialog'
import CheckinDialog from './CheckinDialog'
import TruthCalculator from '@/views/tools/TruthCalculator'

export default {
  name: 'ActivePlan',
  props: {
    household: {
      type: Object,
      required: true
    },
    plan: {
      type: Object,
      required: true
    },
    plans: {
      type: Array,
      default: () => []
    }
  },
  components: {
    DebtChart,
    DebtTable,
    InsuranceSummaryDialog,
    UpdateDraftDialog,
    CheckinDialog,
    TruthCalculator
  },
  data() {
    return {
      showRequiredCapital: true,
      showPolicies: false,
      showDebts: false,
      showCurrentDebtInterest: false,
      showCapitalOptimizationSolution: false,

      showAdvancedButton: false,
      status: null,

      pipelineDialog: false,
      pipelineStatus: null,
      pipelineValue: null,
      pipelineDateMenu: false,
      pipelineDate: null,
      pipelineDateFormatted: null,
      pipelineCloseDateMenu: false,
      pipelineCloseDate: null,
      pipelineCloseDateFormatted: null,
      savingPipeline: false,

      truthCalculatorDialog: false
    }
  },
  mounted() {
    this.status                   = this.plan.status,
    this.pipelineStatus           = (!this.plan.pipeline_status || this.plan.pipeline_status == 'NONE') ? null : this.plan.pipeline_status
    this.pipelineValue            = this.plan.pipeline_value
    
    this.pipelineDate             = this.$moment.unix(this.plan.created_at).format('YYYY-MM-DD')
    this.pipelineCloseDate        = this.$moment.unix(this.plan.pipeline_close).format('YYYY-MM-DD')
  },
  watch: {
    plan: {
      handler(newVal) {
        this.status = newVal.status
      },
      deep: true
    },
    status(newVal, oldVal) {
      if(oldVal && newVal) {
        if(oldVal != 'DRAFT' && oldVal != 'PENDING') return
        if(newVal != 'DRAFT' && newVal != 'PENDING') return
        this.updateStatus(newVal)
      }
    },
    pipelineDate () {
      this.pipelineDateFormatted = this.formatDate(this.pipelineDate)
    },
    pipelineCloseDate () {
      this.pipelineCloseDateFormatted = this.formatDate(this.pipelineCloseDate)
    },
    pipelineStatus(newVal) {
      if(newVal !== 'WIN' && newVal !== 'LOSS') this.pipelineCloseDate = null
      if((newVal === 'WIN' || newVal === 'LOSS') && !this.pipelineCloseDate) this.pipelineCloseDate = this.$moment().format('YYYY-MM-DD')
    }
  },
  computed: {
    moneySaved() {
      if(typeof this.plan.data.total_min_interest === 'undefined') return '-'
      return this.plan.data.total_min_interest - this.plan.data.total_interest
    },
    minTime() {
      return this.summaryTime(this.plan.data.min_freedom_months)
    },
    debtFreeTime() {
      return this.summaryTime(this.plan.data.freedom_months)
    },
    policyReplenishedTime() {
      return this.summaryTime(this.plan.data.payoff_months)
    },
    monthlyCapital() {
      let total = 0
      if(this.plan.data.debts) {
        Object.values(this.plan.data.debts).forEach(d => {
          total += parseFloat(d.minimum_payment)
        })
      }
      if(this.plan.data.policies) {
        Object.values(this.plan.data.policies).forEach(p => {
          if(!p.annual && p.premium) total += (parseFloat(p.premium) / 12)
        })
      }
      return total
    },
    yearlyCapital() {
      let total = 0
      if(this.plan.data.policies) {
        Object.values(this.plan.data.policies).forEach(p => {
          if(p.annual && p.premium) total += parseFloat(p.premium)
        })
      }
      return total
    },
    totalYearly() {
      const monthly = this.monthlyCapital || 0
      const yearly = this.yearlyCapital || 0
      return (monthly * 12) + yearly
    },
    interestVolume() {
      return Math.round(this.plan.data.total_min_interest / this.plan.data.total_debt_principal * 100)
    },
    planInterestVolume() {
      return Math.round(this.plan.data.total_interest / this.plan.data.total_debt_principal * 100)
    },
    year70() {
      if(!this.plan.data.yearly_summary) return null

      let ref = this
      let ageKey = null
      Object.keys(this.plan.data.yearly_summary).forEach(yk => {
        const summary = ref.plan.data.yearly_summary[yk]
        Object.keys(summary.policies).forEach(pk => {
          const policy = summary.policies[pk]
          if(policy.age == 70) ageKey = yk
        })
      })
      return ageKey
    },
    at70() {
      if(!this.plan.data.yearly_summary) return null

      let deathBen  = 0
      let cashVal   = 0
      let savings   = 0

      let key         = this.year70
      if(key === null) return {}

      const summary     = this.plan.data.yearly_summary[ key ]
      savings         = summary.savings
      Object.keys(summary.policies).forEach(pk => {
        const policy = summary.policies[pk]
        deathBen  += policy.death_ben_end
        cashVal   += policy.cash_val_end
      })
      return {
        deathBenefit: deathBen,
        cashValue: cashVal,
        savings: savings
      }
    },
    availableStatuses() {
      return [
        { text: 'Draft', value: 'DRAFT' },
        { text: 'Pending', value: 'PENDING' }
      ]
    },
    oldPlans() {
      let plans = this.plans.filter(p => {
        if(p.algorithm == 'min_only') return false
        if(this.plan.id == p.id) return true
        if(this.plan.id == p.original_id) return true
        if(this.plan.original_id == p.id) return true
        if(this.plan.original_id == p.original_id && p.original_id !== null) return true
        return false
      })
      plans.sort((a,b) => {
        return b.id - a.id
      })
      return plans
    },
    pipelineStatuses() {
      return [
        {
          text: 'Opportunity',
          value: 'OPPORTUNITY'
        },
        {
          text: 'Pending',
          value: 'PENDING'
        },
        {
          text: 'Follow-up',
          value: 'FOLLOW_UP'
        },
        {
          text: 'Win',
          value: 'WIN'
        },
        {
          text: 'Loss',
          value: 'LOSS'
        }
      ]
    }
  },
  methods: {
    summaryTime(months) {
      if(isNaN(months)) return '-'
      if(months < 1) {
        return 'Now'
      }
      if(months < 12) {
        return months + ' Month' + (months !== 1 && 's')
      }
      const years = Math.round(months / 12 * 10) / 10
      return years + ' Year' + (years !== 1 && 's')
    },
    async downloadPlan (lang) {
      if(!lang || lang == 'en') window.open(process.env.VUE_APP_API_BASE_URL + 'download-plan/' + encodeURIComponent(this.household.encrypted_id)  + '?id=' + this.plan.id, '_blank')
      if(lang == 'vn') window.open('https://static.wealthy-iq.com/files/plan_vn1.pdf', '_blank')
    },
    finalize() {
      this.$swal.fire({
        title: 'Confirm',
        html: 'Please confirm that you would like to finalize this plan. The plan will be locked from editing, and a new plan will need to be generated if changes are needed.',
        confirmButtonText: 'Finalize',
        cancelButtonText: 'Cancel'
      })
        .then(result => {
          if(result.value) {
            this.submitFinalization()
          }
        })
    },
    submitFinalization() {
      this.$swal.fire({
        showCancelButton: false,
        allowOutsideClick: false
      }).then(this.$swal.showLoading())

      this.$http.post('household-plans/' + this.plan.id + '/finalize')
        .then(response => {
          this.$emit('on-plan-updated', response.data)
          this.$swal.fire('Plan Finalized', 'Your plan has been finalized', 'success')
        })
        .catch(() => {
          this.$swal.fire('Error', 'There was an error finalizing the plan. Please try again')
        })
    },
    updateStatus(status) {
      this.$swal.fire({
        showCancelButton: false,
        allowOutsideClick: false
      }).then(this.$swal.showLoading())

      this.$http.put('household-plans/' + this.plan.id, {status: status})
        .then(response => {
          this.$emit('on-plan-status-updated', response.data)
          this.$swal.fire({
            title: 'Status Updated',
            timer: 1500,
            icon: 'success',
            showCancelButton: false,
            showConfirmButton: false
          })
        })
        .catch(() => {
          this.$swal.fire('Error', 'There was an error updating the status. Please try again')
        })
    },
    switchPlan(plan) {
      let p = JSON.parse(JSON.stringify(plan))
      p.default = 1
      this.$emit('on-plan-updated', p)
    },
    formatDate (date) {
      if (!date) return null

      const [year, month, day] = date.split('-')
      return `${month}/${day}/${year}`
    },
    parseDate (date) {
      if (!date) return null

      const [month, day, year] = date.split('/')
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    },
    savePipeline() {
      this.savingPipeline = true
      let params = {
        status:     this.pipelineStatus ?? 'NONE',
        close_date: this.pipelineCloseDate ?? null,
        value:      this.pipelineValue ?? 0,
        date:       this.pipelineDate
      }

      this.$http.post('household-plans/' + this.plan.id + '/pipeline', params)
        .then(response => {
          this.$emit('on-plan-status-updated', response.data)
          this.$swal.fire({
            title: 'Pipeline Updated',
            timer: 1500,
            icon: 'success',
            showCancelButton: false,
            showConfirmButton: false
          })
          this.pipelineDialog = false
        })
        .catch(() => {
          this.$swal.fire('Error', 'There was an error updating the pipeline. Please try again')
        })
        .finally(() => {
          this.savingPipeline = false
        })
    }
  }
}
</script>

<style lang="scss" scoped>

  .faSummaryToggle {
    cursor: pointer;
    &:hover {
      background-color: rgba(0,0,0,.1) !important;
    }
  }
  .faCompareToggle {
    cursor: pointer;
    &:hover {
      background-color: rgba(0,0,0,.05);
    }
  }
  .faSummaryCell {
    border-top: thin solid rgba(0,0,0,.05);
    padding: 4px 6px;
    &:first-of-type {
      border-top: none;
    }

    ::v-deep .faSummaryTitle {
      font-size: 14px;
      font-weight: 400;
    }

    ::v-deep .faSummaryDesc {
      font-size: 14px;
      font-weight: 400;
    }

    &.otherPlans {
      cursor: pointer;
    }
    &.otherPlans.active {
      cursor: default !important;
      background-color: rgba(0,255,0,.1) !important;
      a {
        cursor: default;
      }
    }
  }

  .comparisonContainer {
    >div {
      border-top: thin solid rgba(0,0,0,.1);
      &:first-of-type {
        border-top: none;
      }
    }
  }
  .faButton {
    height: 160px;
    width: 200px;
    max-width: 200px;
    min-width: 200px;
    >.faButtonContent {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      display: flex;
      flex-direction: column;
    }
  }
</style>